import { useEffect, useMemo, useState } from "react";
import {
  alertService,
  getLockBUPaymentModes,
  getPaymentMethods,
  getRetentionMethods,
  loaderService,
  GetSettingsLabels,
} from "../services";
import { useLocation, useNavigate } from "react-router-dom";
import { navigation } from "../utils/NavigationLists";
import SidebarStatic from "../components/SidebarStatic";
import SidebarDynamic from "../components/SidebarDynamic";
import { UserInterface } from "../interfaces/UserInterface";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  clearShipmentCreate,
  login,
  logout,
  setPaymentMethods,
  setRetentionMethods,
  setUserLocation,
} from "../store/slices";
import {
  CreateOrUpdateSession,
  logoutSession,
} from "../services/authetication";
import {
  SessionInterface,
  ResponseInterface,
  BusinessUnitInterface,
  PaymentMethodInterface,
} from "../interfaces";

const Layout = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState<number>(0);
  const { pathname: path } = useLocation();
  const dispatch = useAppDispatch();

  const userLogged = useAppSelector((state) => state.user.user);
  const businessUnit = useAppSelector((state) => state.user.businessUnit);
  const userSession = useAppSelector((state) => state.user.userSession);
  const inmutable = useAppSelector((state) => state.inmutable);

  useEffect(() => {
    const refreshLabelConfig = async () => {
      if (businessUnit?.code) {
        try {
          const currentLabelFormat = await GetSettingsLabels(businessUnit.code);
          if (businessUnit.labelFormat !== currentLabelFormat) {
            const updatedBU = { ...businessUnit, labelFormat: currentLabelFormat };
            dispatch(setUserLocation(updatedBU));
          }
        } catch (e) {
          console.error("No se pudo sincronizar config de etiqueta", e);
        }
      }
    };
    refreshLabelConfig();
  }, [path]);

  const filteredNavigation = useMemo(() => {
    return navigation.filter((nav) => {
      return (
        (!nav.rolesRequired ||
          (!!userLogged && nav.rolesRequired?.includes(userLogged.roleName))) &&
        (!nav.filter || nav.filter(userLogged ?? undefined, businessUnit))
      );
    });
  }, [businessUnit, userLogged]);

  useEffect(() => {
    const section = "/" + path.split("/")[1];
    const index = navigation.findIndex((item) => item.href === section);

    setCurrentItemIndex(index);
  }, [path]);

  const handleLogout = async () => {
    if (!userLogged || !userSession) return;
    const response = await logoutSession(
      userLogged.userID,
      userSession.sessionID
    );
    if (!response.didError) dispatch(logout());
  };

  const handleBusinessUnitChange = async (
    user: UserInterface,
    businessUnit: BusinessUnitInterface
  ) => {
    loaderService.start();
    const userSessionResponse: ResponseInterface<SessionInterface> =
      await CreateOrUpdateSession(
        user.userID,
        inmutable.appData.applicationCode,
        businessUnit.code
      );
    const paymentMethodsResponse: PaymentMethodInterface[] =
      await getPaymentMethods(businessUnit.code, user.userLogin);
    const retentionMethodsResponse: PaymentMethodInterface[] =
      await getRetentionMethods(businessUnit.code, user.userLogin);
    const paymentModes = await getLockBUPaymentModes(businessUnit.code);

    loaderService.stop();
    if (
      !userSessionResponse.didError &&
      !!userSessionResponse.model &&
      !paymentModes.didError &&
      !!paymentModes.model
    ) {
      let userSession = userSessionResponse.model;
      dispatch(clearShipmentCreate());
      dispatch(login({ user: user, session: userSession }));
      dispatch(setUserLocation(businessUnit));
      dispatch(setPaymentMethods(paymentMethodsResponse));
      dispatch(setRetentionMethods(retentionMethodsResponse));
      navigate("/dashboard");
    } else {
      alertService.error(userSessionResponse.errorMessage);
    }
  };

  return (
    <>
      <SidebarDynamic
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        navigationItems={filteredNavigation}
        currentItemIndex={currentItemIndex}
        setCurrentItemIndex={setCurrentItemIndex}
        handleLogout={handleLogout}
        handleBusinessUnitChange={handleBusinessUnitChange}
      />
      <SidebarStatic
        navigationItems={filteredNavigation}
        currentItemIndex={currentItemIndex}
        setCurrentItemIndex={setCurrentItemIndex}
        handleLogout={handleLogout}
        handleBusinessUnitChange={handleBusinessUnitChange}
      />
    </>
  );
};

export default Layout;
